import { useState, useRef, useCallback } from 'react';

interface CropFileMeta {
  fileName: string;
  mimeType: string;
}

interface UsePhotoCropFlowOptions {
  validateImageFile: (file: File) => string | null;
  onUpload: (file: File) => Promise<void>;
  onValidationError?: (message: string) => void;
}

export function usePhotoCropFlow({
  validateImageFile,
  onUpload,
  onValidationError,
}: UsePhotoCropFlowOptions) {
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropFileMeta, setCropFileMeta] = useState<CropFileMeta>({
    fileName: 'profile-photo.jpg',
    mimeType: 'image/jpeg',
  });
  const [isApplyingCrop, setIsApplyingCrop] = useState(false);
  const cropSrcRef = useRef<string | null>(null);

  const cleanupCropSrc = useCallback(() => {
    if (cropSrcRef.current) {
      URL.revokeObjectURL(cropSrcRef.current);
      cropSrcRef.current = null;
    }
  }, []);

  const closeCrop = useCallback(() => {
    setIsCropOpen(false);
    cleanupCropSrc();
    setCropImageSrc(null);
  }, [cleanupCropSrc]);

  const openCropForFile = useCallback(
    (file: File): boolean => {
      const validationError = validateImageFile(file);
      if (validationError) {
        onValidationError?.(validationError);
        return false;
      }

      cleanupCropSrc();
      const src = URL.createObjectURL(file);
      cropSrcRef.current = src;
      setCropImageSrc(src);
      setCropFileMeta({
        fileName: file.name.replace(/\.[^.]+$/, '') + '.jpg',
        mimeType: file.type === 'image/png' ? 'image/png' : 'image/jpeg',
      });
      setIsCropOpen(true);
      return true;
    },
    [validateImageFile, onValidationError, cleanupCropSrc]
  );

  const handleCropApply = useCallback(
    async (croppedFile: File) => {
      setIsApplyingCrop(true);
      try {
        await onUpload(croppedFile);
        closeCrop();
      } finally {
        setIsApplyingCrop(false);
      }
    },
    [onUpload, closeCrop]
  );

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        openCropForFile(file);
      }
      event.target.value = '';
    },
    [openCropForFile]
  );

  return {
    isCropOpen,
    cropImageSrc,
    cropFileMeta,
    isApplyingCrop,
    closeCrop,
    handleCropApply,
    openCropForFile,
    handleFileInputChange,
  };
}
